import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { Input } from "../../../components/Input";
import { SelectInput } from "../../../components/SelectInput";
import styled from "styled-components";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { useTranslation } from "react-i18next";
import type { IntegrationViva } from "@KassaroAB/shared/types/integrations/integration";
import type { RestUpdateIntegration } from "@KassaroAB/shared/types/integrations/integration";
import type { ChainId, StoreId } from "@KassaroAB/shared/types/ids";
import {
  useCreateVivaIntegrationMutation,
  useUpdateIntegrationMutation,
} from "../../../query/integrationsQuery";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { useIsAdmin } from "../../../hooks/useIsAdmin";
import { InfoBox } from "../../../components/InfoBox";
import { VivaOnboardingStatus } from "@KassaroAB/shared/types/enums/VivaOnboardingStatus";
import { calculateVivaIsvFee } from "@KassaroAB/shared/utils/vivaISVFeeCalculation";
import { formatMoney } from "../../../utils/formatMoney";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const AdminHint = styled.div`
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  margin-top: ${spacing.x2}px;
  min-width: 0;
  max-width: 100%;
`;

const StyledForm = styled(Form)`
  min-width: 0;
  max-width: 100%;
`;

const vivaOnboardingStatusLabelKey = (status: VivaOnboardingStatus) => {
  switch (status) {
    case VivaOnboardingStatus.STARTED:
      return "integrationDetails.vivaOnboardingStatusStarted";
    case VivaOnboardingStatus.COMPLETED:
      return "integrationDetails.vivaOnboardingStatusCompleted";
    default:
      return "integrationDetails.vivaOnboardingStatusUnknown";
  }
};

const getVivaOnboardingUrl = (
  value: IntegrationViva | (Record<string, unknown> & { onboardingUrl?: string }),
): string | undefined => {
  const direct = (value as IntegrationViva).onboardingUrl;
  if (typeof direct === "string" && direct.length > 0) {
    return direct;
  }
  const alt = (value as Record<string, unknown>).onboardingLink;
  if (typeof alt === "string" && alt.length > 0) {
    return alt;
  }
  return undefined;
};

const OnboardingUrlPanel = styled.div`
  margin-top: ${spacing.x2}px;
  padding: ${spacing.x2}px ${spacing.x3}px;
  background-color: ${colors.infoBackground};
  border-radius: ${borderRadius.regular}px;
  border: 1px solid ${colors.info};
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-all;
`;

const OnboardingUrlText = styled.a`
  display: block;
  min-width: 0;
  max-width: 100%;
  font-size: ${fontSizes.regular}px;
  color: ${colors.infoText};
  font-weight: 600;
  overflow-wrap: anywhere;
  word-break: break-all;
`;

const DEFAULT_ISV_PERCENTAGE = 0.42;
const ISV_RATE_PRESETS = [0.12, 0.22, 0.32, 0.42];
const CUSTOM_RATE_OPTION = "custom";
const VIVA_BASE_PERCENTAGE = 0.57;
const CREDIT_CARD_SURCHARGE = 0.1;
const COMPANY_CARD_SURCHARGE = 1;
const RESELLER_SHARE_RATE = 0.5;
const EXAMPLE_SALE_AMOUNT_MINOR = 100000;

const SplitPanel = styled.div`
  padding: ${spacing.x2}px ${spacing.x3}px;
  background-color: ${colors.infoBackground};
  border-radius: ${borderRadius.regular}px;
  border: 1px solid ${colors.info};
`;

const SplitHeading = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 700;
  color: ${colors.infoText};
  margin-bottom: ${spacing.x1}px;
`;

const SplitRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  padding: ${spacing.x05}px 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.infoText};
`;

const SplitDivider = styled.div`
  margin: ${spacing.x1}px 0;
  border-top: 1px solid ${colors.info};
`;

const SplitValue = styled.span`
  font-weight: 700;
  white-space: nowrap;
`;

const CustomRateSection = styled.div`
  margin-top: ${spacing.x1}px;
  padding-left: ${spacing.x2}px;
  border-left: 2px solid ${colors.border};
`;

const roundPercentage = (percentage: number): number =>
  parseFloat(percentage.toFixed(3));

const formatPercentage = (percentage: number): string =>
  `${roundPercentage(percentage)}%`;

const formatRate = (
  percentage: number,
  fixedAmountMinor: number,
  currency: Currency,
): string =>
  fixedAmountMinor === 0
    ? formatPercentage(percentage)
    : `${formatPercentage(percentage)} + ${formatMoney({ amount: fixedAmountMinor, currency })}`;

interface VivaFormProps {
  storeId: StoreId;
  chainId?: ChainId;
  integration?: IntegrationViva;
  onSuccess: () => void;
  onError: () => void;
  onCancel: () => void;
}

export const VivaForm = ({
  storeId,
  chainId,
  integration,
  onSuccess,
  onError,
  onCancel,
}: VivaFormProps) => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { data: store } = useGetStoreQuery(storeId);
  const { mutate: createMutation } = useCreateVivaIntegrationMutation(
    chainId ?? store!.chainId,
  );
  const { mutate: updateMutation } = useUpdateIntegrationMutation(
    chainId ?? store?.chainId ?? null,
  );
  const [createdIntegration, setCreatedIntegration] = useState<IntegrationViva | null>(null);

  const onboardingUrlForEdit = integration ? getVivaOnboardingUrl(integration) : undefined;

  const defaultIsv = integration?.isvFee;
  const [isCustomRate, setIsCustomRate] = useState(
    !ISV_RATE_PRESETS.includes(defaultIsv?.percentage ?? DEFAULT_ISV_PERCENTAGE),
  );

  const form = useForm({
    defaultValues: {
      name: integration?.name || "Viva",
      merchantEmail: store?.contactEmail ?? "",
      merchantId: integration?.merchantId || "",
      accountId: integration?.accountId || "",
      isvPercentage: defaultIsv?.percentage ?? DEFAULT_ISV_PERCENTAGE,
      isvAmount: (defaultIsv?.amount.amount ?? 0),
    },
    onSubmit: ({ value }) => {
      const companyCurrency = store?.currency ?? Currency.SEK;
      const isvFee = {
        percentage: value.isvPercentage,
        amount: {
          amount: Number.isNaN(value.isvAmount) ? 0 : value.isvAmount,
          currency: companyCurrency,
        },
      };
      if (integration) {
        const base: Partial<RestUpdateIntegration> = {
          type: IntegrationType.VIVA,
          name: value.name,
          isvFee,
        };
        const payload: Partial<RestUpdateIntegration> = isAdmin
          ? {
              ...base,
              merchantId: value.merchantId,
              accountId: value.accountId,
            }
          : base;
        updateMutation(
          { integrationId: integration.id, integration: payload },
          { onSuccess: () => onSuccess(), onError: () => onError() },
        );
      } else {
        if (!chainId) {
          onError();
          return;
        }
        createMutation(
          {
            name: value.name,
            merchantEmail: value.merchantEmail,
            isvFee,
          },
          {
            onSuccess: (data) => {
              const created = data.integration as IntegrationViva;
              const url =
                data.onboardingLink || getVivaOnboardingUrl(created);
              setCreatedIntegration({
                ...created,
                onboardingUrl: url ?? created.onboardingUrl,
              });
            },
            onError: () => onError(),
          },
        );
      }
    },
  });

  if (createdIntegration && !integration) {
    const onboardingUrl = getVivaOnboardingUrl(createdIntegration);
    const onboardingStatus = createdIntegration.onboardingStatus;
    return (
      <Container>
        <InfoBox variant="success">{t("integrationDetails.vivaCreatedNextSteps")}</InfoBox>
        {onboardingUrl && (
          <FormGroup>
            <FormLabel>{t("integrationDetails.vivaOnboardingLinkHeading")}</FormLabel>
            <OnboardingUrlPanel>
              <OnboardingUrlText
                href={onboardingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {onboardingUrl}
              </OnboardingUrlText>
            </OnboardingUrlPanel>
            <div>
              <Button
                type="button"
                onClick={() => {
                  window.open(onboardingUrl, "_blank", "noopener,noreferrer");
                }}
              >
                {t("integrationDetails.vivaOpenOnboardingLink")}
              </Button>
            </div>
          </FormGroup>
        )}
        {onboardingStatus && (
          <FormGroup>
            <FormLabel>{t("integrationDetails.onboardingStatusLabel")}</FormLabel>
            <div style={{ marginTop: 4 }}>
              {t(vivaOnboardingStatusLabelKey(onboardingStatus))}
            </div>
          </FormGroup>
        )}
        <FormActions>
          <Button type="button" $color="secondary" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button type="button" onClick={() => onSuccess()}>
            {t("integrationDetails.vivaOnboardingDone")}
          </Button>
        </FormActions>
      </Container>
    );
  }

  return (
    <StyledForm
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {!integration && !chainId && (
        <FormGroup>
          <InfoBox variant="warning">{t("integrationDetails.vivaChainRequired")}</InfoBox>
        </FormGroup>
      )}
      {integration &&
        (integration.onboardingStatus || onboardingUrlForEdit) && (
          <FormGroup>
            <InfoBox variant="info">
              {integration.onboardingStatus && (
                <div style={{ marginBottom: onboardingUrlForEdit ? 8 : 0 }}>
                  <strong>{t("integrationDetails.onboardingStatusLabel")}</strong>{" "}
                  {t(vivaOnboardingStatusLabelKey(integration.onboardingStatus))}
                </div>
              )}
              {onboardingUrlForEdit && (
                <div>
                  <strong>{t("integrationDetails.onboardingUrlLabel")}</strong>
                  <OnboardingUrlText
                    href={onboardingUrlForEdit}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {onboardingUrlForEdit}
                  </OnboardingUrlText>
                </div>
              )}
            </InfoBox>
          </FormGroup>
        )}
      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => (!value ? t("integrationDetails.nameRequired") : undefined),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.nameLabel")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.namePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      {!integration && (
        <form.Field
          name="merchantEmail"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t("integrationDetails.merchantEmailRequired");
              const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
              return ok ? undefined : t("integrationDetails.merchantEmailInvalid");
            },
          }}
        >
          {(field) => (
            <FormGroup>
              <FormLabel>{t("integrationDetails.merchantEmailLabel")}</FormLabel>
              <Input
                type="email"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                required
                placeholder={t("integrationDetails.merchantEmailPlaceholder")}
              />
              {field.state.meta.errors.length > 0 && (
                <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
              )}
            </FormGroup>
          )}
        </form.Field>
      )}

      <form.Field
        name="isvPercentage"
        validators={{
          onChange: ({ value }) => {
            if (value === undefined || Number.isNaN(value)) {
              return t("integrationDetails.isvPercentageRequired");
            }
            if (value < 0) {
              return t("integrationDetails.isvPercentageBelowBase", {
                base: formatPercentage(VIVA_BASE_PERCENTAGE),
              });
            }
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.privateCardsLabel")}</FormLabel>
            <SelectInput
              value={isCustomRate ? CUSTOM_RATE_OPTION : String(field.state.value)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === CUSTOM_RATE_OPTION) {
                  setIsCustomRate(true);
                  return;
                }
                setIsCustomRate(false);
                field.handleChange(parseFloat(v));
              }}
            >
              {ISV_RATE_PRESETS.map((preset, index) => (
                <option key={preset} value={preset}>
                  {t(`integrationDetails.isvPercentageTier${index + 1}`, {
                    rate: formatPercentage(VIVA_BASE_PERCENTAGE + preset),
                  })}
                </option>
              ))}
              <option value={CUSTOM_RATE_OPTION}>
                {t("integrationDetails.isvPercentageCustom")}
              </option>
            </SelectInput>
            {isCustomRate && (
              <CustomRateSection>
                <FormLabel>
                  {t("integrationDetails.isvPercentageCustomLabel")}
                </FormLabel>
                <Input
                  type="number"
                  step="any"
                  min={VIVA_BASE_PERCENTAGE}
                  value={
                    Number.isNaN(field.state.value)
                      ? ""
                      : roundPercentage(VIVA_BASE_PERCENTAGE + field.state.value)
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    field.handleChange(
                      v === ""
                        ? Number.NaN
                        : roundPercentage(parseFloat(v) - VIVA_BASE_PERCENTAGE),
                    );
                  }}
                  onBlur={field.handleBlur}
                  required
                  placeholder={t("integrationDetails.isvPercentagePlaceholder")}
                />
              </CustomRateSection>
            )}
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="isvAmount">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.isvAmountLabel")}</FormLabel>
            <Input
              type="number"
              step={1}
              min={0}
              value={Number.isNaN(field.state.value) ? "" : field.state.value}
              onChange={(e) => {
                const v = e.target.value;
                field.handleChange(v === "" ? Number.NaN : parseInt(v));
              }}
              onBlur={field.handleBlur}
              placeholder={t("integrationDetails.isvAmountOrePlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Subscribe
        selector={(state) =>
          [state.values.isvPercentage, state.values.isvAmount] as const
        }
      >
        {([isvPercentage, rawIsvAmount]) => {
          if (Number.isNaN(isvPercentage)) {
            return null;
          }
          const isvAmount = Number.isNaN(rawIsvAmount) ? 0 : rawIsvAmount;
          const currency = store?.currency ?? Currency.SEK;
          const exampleFeeMinor = calculateVivaIsvFee(EXAMPLE_SALE_AMOUNT_MINOR, {
            percentage: isvPercentage,
            amount: { amount: isvAmount, currency },
          });
          const sharePercentage = isvPercentage * RESELLER_SHARE_RATE;
          const shareFixedMinor = Math.round(isvAmount * RESELLER_SHARE_RATE);
          const privateCardPercentage = VIVA_BASE_PERCENTAGE + isvPercentage;
          const exampleTotalMinor =
            Math.round(
              EXAMPLE_SALE_AMOUNT_MINOR * (privateCardPercentage / 100),
            ) + isvAmount;
          return (
            <FormGroup>
              <SplitPanel>
                <SplitHeading>
                  {t("integrationDetails.feeSplitCardRatesHeading")}
                </SplitHeading>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitPrivateCards")}</span>
                  <SplitValue>
                    {formatRate(privateCardPercentage, isvAmount, currency)}
                  </SplitValue>
                </SplitRow>
                <SplitRow>
                  <span>
                    {t("integrationDetails.feeSplitCreditCards", {
                      surcharge: formatPercentage(CREDIT_CARD_SURCHARGE),
                    })}
                  </span>
                  <SplitValue>
                    {formatRate(
                      privateCardPercentage + CREDIT_CARD_SURCHARGE,
                      isvAmount,
                      currency,
                    )}
                  </SplitValue>
                </SplitRow>
                <SplitRow>
                  <span>
                    {t("integrationDetails.feeSplitCompanyCards", {
                      surcharge: formatPercentage(COMPANY_CARD_SURCHARGE),
                    })}
                  </span>
                  <SplitValue>
                    {formatRate(
                      privateCardPercentage + COMPANY_CARD_SURCHARGE,
                      isvAmount,
                      currency,
                    )}
                  </SplitValue>
                </SplitRow>
                <SplitDivider />
                <SplitHeading>
                  {t("integrationDetails.feeSplitHeading")}
                </SplitHeading>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitVivaBase")}</span>
                  <SplitValue>
                    {formatPercentage(VIVA_BASE_PERCENTAGE)}
                  </SplitValue>
                </SplitRow>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitIsvFee")}</span>
                  <SplitValue>
                    {formatRate(isvPercentage, isvAmount, currency)}
                  </SplitValue>
                </SplitRow>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitResellerShare")}</span>
                  <SplitValue>
                    {formatRate(sharePercentage, shareFixedMinor, currency)}
                  </SplitValue>
                </SplitRow>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitKassaroShare")}</span>
                  <SplitValue>
                    {formatRate(sharePercentage, shareFixedMinor, currency)}
                  </SplitValue>
                </SplitRow>
                <SplitDivider />
                <SplitHeading>
                  {t("integrationDetails.feeSplitExampleHeading", {
                    amount: formatMoney({
                      amount: EXAMPLE_SALE_AMOUNT_MINOR,
                      currency,
                    }),
                  })}
                </SplitHeading>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitExampleTotal")}</span>
                  <SplitValue>
                    {formatMoney({ amount: exampleTotalMinor, currency })}
                  </SplitValue>
                </SplitRow>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitExampleFee")}</span>
                  <SplitValue>
                    {formatMoney({ amount: exampleFeeMinor, currency })}
                  </SplitValue>
                </SplitRow>
                <SplitRow>
                  <span>{t("integrationDetails.feeSplitExampleShare")}</span>
                  <SplitValue>
                    {formatMoney({
                      amount: Math.round(
                        exampleFeeMinor * RESELLER_SHARE_RATE,
                      ),
                      currency,
                    })}
                  </SplitValue>
                </SplitRow>
              </SplitPanel>
            </FormGroup>
          );
        }}
      </form.Subscribe>

      {integration && (
        <>
          <form.Field
            name="merchantId"
            validators={{
              onChange: ({ value }) =>
                isAdmin && !value ? t("integrationDetails.merchantIdRequired") : undefined,
            }}
          >
            {(field) => (
              <FormGroup>
                <FormLabel>{t("integrationDetails.merchantIdLabel")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={!isAdmin}
                  required={isAdmin}
                  placeholder={t("integrationDetails.merchantIdPlaceholder")}
                />
                {!isAdmin && (
                  <AdminHint>{t("integrationDetails.vivaAdminOnlyIdsHint")}</AdminHint>
                )}
                {field.state.meta.errors.length > 0 && (
                  <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
                )}
              </FormGroup>
            )}
          </form.Field>

          <form.Field
            name="accountId"
            validators={{
              onChange: ({ value }) =>
                isAdmin && !value ? t("integrationDetails.accountIdRequired") : undefined,
            }}
          >
            {(field) => (
              <FormGroup>
                <FormLabel>{t("integrationDetails.accountIdLabel")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={!isAdmin}
                  required={isAdmin}
                  placeholder={t("integrationDetails.accountIdPlaceholder")}
                />
                {!isAdmin && (
                  <AdminHint>{t("integrationDetails.vivaAdminOnlyIdsHint")}</AdminHint>
                )}
                {field.state.meta.errors.length > 0 && (
                  <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
                )}
              </FormGroup>
            )}
          </form.Field>
        </>
      )}

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={(!integration && !chainId) || !store}>
          {integration ? t("common.update") : t("common.create")}
        </Button>
      </FormActions>
    </StyledForm>
  );
};
